/**
 * The mark that stands for a game.
 *
 * Presentation, exactly as `avatarEmoji` is: the catalogue names the games and
 * says what family they belong to, and this decides what they look like. A
 * client that has just fetched a game nobody drew art for still has to put
 * something on the card.
 *
 * ## This used to draw two real cards
 *
 * And that had one genuine advantage worth recording, because it is what we
 * gave up: the cards came from the equipped deck, so game tiles were **skinned
 * for free** — buy the medieval deck and the library changed with it.
 *
 * It lost on the thing tiles are actually for. Two cards at tile size are two
 * small white rectangles, and at a glance every game looked the same; the
 * mockups draw a distinct mark per game for exactly that reason, and a red
 * heart or a gold crown is recognisable at 40px in a way "Q♠ A♥" is not.
 *
 * If skinned tiles are ever wanted back, the way to have both is a per-deck
 * icon set rather than a return to drawing cards.
 *
 * ## The fallback is the family, not a guess
 *
 * A game nobody has drawn a mark for gets its **family's** mark, which is a
 * property the catalogue already publishes. A shedding game nobody has drawn
 * gets a shedding game's look: wrong in detail, right in kind.
 */

import { GAME_ART } from './game-art-files.js';
import type { IconName } from './Icon.js';

/** How a mark is painted. Suits have their own colours; everything else is gold. */
export type GameArtTone = 'gold' | 'red' | 'dark';

export interface GameArt {
  readonly icon: IconName;
  readonly tone: GameArtTone;
}

const BY_GAME: Record<string, GameArt> = {
  // The suit the game is named for, in the colour it is printed in.
  hearts: { icon: 'heart', tone: 'red' },
  spades: { icon: 'spade', tone: 'dark' },
  // Eights are wild, and are the whole point of the game.
  'crazy-eights': { icon: 'pair-of-eights', tone: 'gold' },
  // Last player standing wears the crown.
  'knockout-whist': { icon: 'crown', tone: 'gold' },
  // Named for no suit in particular, so it takes the one nobody else has.
  'oh-hell': { icon: 'diamond', tone: 'red' },
  // Cards face down and a claim about them: the pair says "what are these?".
  cheat: { icon: 'pair-of-sevens', tone: 'gold' },
  // Asking for a rank you are collecting.
  'go-fish': { icon: 'pair-of-aces', tone: 'gold' },
  // Sets and runs - a handful of cards rather than one.
  rummy: { icon: 'pair-of-cards', tone: 'gold' },
  // Two cards in the hole.
  'texas-holdem': { icon: 'pair-of-aces', tone: 'red' },

  /*
   * The patiences, which shared a mark until they shared a shelf.
   *
   * Three games under one heading all drawing `pair-of-cards` made the
   * Solitaire row look like one game printed three times - the family
   * fallback is fine scattered through a grid and useless when the family IS
   * the section. Each takes something of its own until it has real art.
   */
  // Build to the king, four times over.
  klondike: { icon: 'crown', tone: 'gold' },
  // A triangle of cards, and the spade is what the last one rests on.
  pyramid: { icon: 'spade', tone: 'dark' },
  // Up one or down one, over and over - a run rather than a pair.
  golf: { icon: 'pair-of-sevens', tone: 'red' },
};

const BY_FAMILY: Record<string, GameArt> = {
  shedding: { icon: 'pair-of-cards', tone: 'gold' },
  patience: { icon: 'pair-of-cards', tone: 'dark' },
  'trick-taking': { icon: 'spade', tone: 'dark' },
  melds: { icon: 'pair-of-cards', tone: 'gold' },
  poker: { icon: 'pair-of-aces', tone: 'red' },
};

const FALLBACK: GameArt = { icon: 'pair-of-cards', tone: 'gold' };

export function gameArt(gameId: string, family: string): GameArt {
  return BY_GAME[gameId] ?? BY_FAMILY[family] ?? FALLBACK;
}

/**
 * The game's own picture, or null where there is not one yet.
 *
 * One function because two screens ask: the library card and the detail page
 * behind it. They asked separately for a while and gave different answers -
 * the card drew the painting and the page one tap away drew the fallback mark,
 * which is the same game wearing two faces on consecutive screens (X4).
 *
 * `GAME_ART` is generated from what is actually in `public/art/games`, so this
 * cannot promise a file that is not there.
 */
export function heroArt(gameId: string): string | null {
  return GAME_ART.includes(gameId) ? `/art/games/${gameId}.webp` : null;
}
